import { useEffect, useState } from "react";
import FlatRatePlan from "./FlatRatePlan";
import InterchangePlusPlan from "./InterchangePlusPlan";
import { AccountHolderRatePlan } from "../../services/accountService";
import { useSnackbar } from "../../components/Snackbar";
import useCurrentUserAccount from "../../hooks/data/queries/useCurrentUserAccount";
import Panel from "./Panel";
import Tab from "../../components/Tab";
import ChangePlanConfirmationModal from "./ChangePlanConfirmationModal";
import { csrAdminRoles } from "../../components/HasAccess";
import EditControls from "./EditControls";
import { useRatePlansContext } from "../../hooks/data/providers/ratePlansContext";
import useUpdateRatePlansMutation from "../../hooks/data/mutations/useUpdateRatePlansMutation";

export type RatePlan = {
  id: string;
  label: string;
  data?: AccountHolderRatePlan;
};

let ratePlanTypes = ["Flat Rate Plan"];
ratePlanTypes.push("Interchange ++ Plan");

export type RatePlanType = typeof ratePlanTypes[number];

const countriesThatAllowInterchange = ["US", "PR", "MP", "UM", "VI"];

type Props = {
  accountHolderCode: string;
  accountHolderId: string;
  accountHolderRatePlans: AccountHolderRatePlan[];
  useInterchangeRate: boolean;
  hasMonthlyFee: boolean;
  monthlyFee: string;
  isMasterSurchargeEnabled: boolean;
  inBalancePlatform: boolean;
  useTechFee: boolean;
  useConvFee: boolean;
  country: string;
  isLoading: boolean;
};

function RatePlans({
  accountHolderCode,
  accountHolderId,
  accountHolderRatePlans,
  useInterchangeRate,
  hasMonthlyFee,
  monthlyFee,
  isMasterSurchargeEnabled,
  inBalancePlatform,
  useTechFee,
  useConvFee,
  country,
  isLoading,
}: Props): JSX.Element {
  const { data: currentUserAccount } = useCurrentUserAccount();
  const isCSRAdmin = currentUserAccount?.role
    ? csrAdminRoles.includes(currentUserAccount?.role)
    : false;
  const [tabIndex, setTabIndex] = useState(-1);
  const [showConfirmChangeModal, setShowConfirmChangeModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const { openSnackbar } = useSnackbar({
    duration: 30000,
    position: "top",
  });
  const {
    modifiedRatePlans: ratePlansCtx,
    reset: resetRatePlansCtx,
    saveButtonEnabled,
    updateRatePlanType,
  } = useRatePlansContext();
  const { mutate: updateRatePlans, isLoading: isUpdatingRatePlans } =
    useUpdateRatePlansMutation(accountHolderCode);

  useEffect(() => {
    setTabIndex(useInterchangeRate ? 1 : 0);
  }, [useInterchangeRate]);

  function handleTabChange(index = 0) {
    setTabIndex(index);
    const newType = ratePlanTypes[index] as RatePlanType;
    updateRatePlanType(newType);
  }

  function handleUpdateRatePlans() {
    updateRatePlans(
      {
        accountHolderId: accountHolderId,
        ratePlans: ratePlansCtx,
      },
      {
        onSuccess() {
          openSnackbar("Rate plans were successfully updated", {
            type: "success",
          });
          setIsEditing(false);
        },
        onError() {
          openSnackbar("Failed to update rate plans. Please try again.", {
            type: "error",
          });
        },
      }
    );
  }

  return (
    <>
      <Panel className="col-span-7">
        <Panel.Header title="Rate Plans">
          {isCSRAdmin && (
            <EditControls
              customId="editRatePlansButton"
              isEditing={isEditing}
              isSaving={isUpdatingRatePlans}
              editButtonProps={{
                disabled: isLoading,
                onClick: () => {
                  setIsEditing(true);
                },
              }}
              saveButtonProps={{
                disabled:
                  isLoading || !saveButtonEnabled || isUpdatingRatePlans,
                onClick: () => handleUpdateRatePlans(),
              }}
              cancelButtonProps={{
                onClick: () => {
                  setIsEditing(false);
                  // Set the ratePlans state back to the initial state
                  resetRatePlansCtx();
                },
              }}
            />
          )}
        </Panel.Header>
        <Panel.Content>
          {inBalancePlatform &&
          countriesThatAllowInterchange.includes(country) ? (
            <Tab.Group selectedIndex={tabIndex} onChange={handleTabChange}>
              <Tab.List>
                {ratePlanTypes.map((type, i) => (
                  <Tab
                    key={i}
                    index={i}
                    disabled={!isCSRAdmin}
                    isLoading={isLoading}
                  >
                    {type}{" "}
                    {i === (useInterchangeRate ? 1 : 0) ? "(ACTIVE)" : ""}
                  </Tab>
                ))}
              </Tab.List>
              <Tab.Panels>
                <FlatRatePlan
                  accountHolderCode={accountHolderCode}
                  accountHolderId={accountHolderId}
                  accountHolderRatePlans={accountHolderRatePlans}
                  ratePlanType="Flat Rate Plan"
                  isMasterSurchargeEnabled={isMasterSurchargeEnabled}
                  isLoading={isLoading}
                  isEditing={isEditing}
                />
                <InterchangePlusPlan
                  accountHolderCode={accountHolderCode}
                  accountHolderId={accountHolderId}
                  accountHolderRatePlans={accountHolderRatePlans}
                  useInterchangeRate={useInterchangeRate}
                  isLoading={isLoading}
                  hasMonthlyFee={hasMonthlyFee}
                  ratePlanType="Interchange ++ Plan"
                  monthlyFee={monthlyFee}
                  isEditing={isEditing}
                />
              </Tab.Panels>
            </Tab.Group>
          ) : (
            <div className="flex flex-col">
              <h3 className="font-semibold">Flat Rate Plan</h3>
              <FlatRatePlan
                accountHolderCode={accountHolderCode}
                accountHolderId={accountHolderId}
                accountHolderRatePlans={accountHolderRatePlans}
                ratePlanType="Flat Rate Plan"
                isMasterSurchargeEnabled={isMasterSurchargeEnabled}
                isLoading={isLoading}
                isEditing={isEditing}
              />
            </div>
          )}
        </Panel.Content>
      </Panel>
    </>
  );
}

export default RatePlans;
